// src/resources/commentReports/CommentReportActionsField.js
import * as React from 'react';
import { useRecordContext } from 'react-admin';
import {
    Button,
    Stack,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    TextField,
    Typography,
} from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import useModerationAction from '../../hooks/useModerationAction';
import {
    COMMENT_REPORT_STATUS_LABELS,
    COMMENT_REPORT_STATUS_CONFIG,
} from './constants';
import {
    moderateReportedComment,
    updateCommentReportStatus,
    COMMENT_MODERATION_ACTIONS,
} from '../../services/commentReports';

const STATUS_ACTIONS = COMMENT_REPORT_STATUS_CONFIG.filter(({ status }) => status !== 'NEW');

const findStatusLabel = (status) =>
    COMMENT_REPORT_STATUS_LABELS[status] || status || 'Statut';

export default function CommentReportActionsField({ variant = 'list', record: recordFromProps }) {
    const contextRecord = useRecordContext();
    const record = recordFromProps || contextRecord;
    const { loading, run } = useModerationAction();
    const [statusDialog, setStatusDialog] = React.useState(null);
    const [statusNote, setStatusNote] = React.useState('');
    const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
    const [deleteReason, setDeleteReason] = React.useState('');
    const buttonSize = variant === 'show' ? 'medium' : 'small';

    if (!record) return null;

    const openStatusDialog = (event, status) => {
        event?.stopPropagation?.();
        setStatusDialog({ status });
        setStatusNote('');
    };

    const closeStatusDialog = () => {
        setStatusDialog(null);
        setStatusNote('');
    };

    const submitStatusChange = async () => {
        if (!statusDialog?.status) return;
        await run({
            id: record.id,
            request: () =>
                updateCommentReportStatus(record.id, {
                    status: statusDialog.status,
                    note: statusNote?.trim() || null,
                }),
            successMessage: `Signalement marqué "${findStatusLabel(statusDialog.status)}"`,
            onSuccess: closeStatusDialog,
        });
    };

    const openDeleteDialog = (event) => {
        event?.stopPropagation?.();
        setDeleteDialogOpen(true);
        setDeleteReason('');
    };

    const closeDeleteDialog = () => {
        setDeleteDialogOpen(false);
        setDeleteReason('');
    };

    const submitDeleteAction = async () => {
        await run({
            id: record.id,
            request: () =>
                moderateReportedComment(record.id, {
                    action: COMMENT_MODERATION_ACTIONS.REMOVE,
                    note: deleteReason?.trim() || null,
                }),
            successMessage: 'Commentaire supprimé',
            onSuccess: closeDeleteDialog,
        });
    };

    return (
        <>
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                {STATUS_ACTIONS.map(({ status, label, color }) => (
                    <Button
                        key={status}
                        size={buttonSize}
                        color={color === 'default' ? 'primary' : color}
                        variant="outlined"
                        disabled={loading}
                        onClick={(event) => openStatusDialog(event, status)}
                    >
                        {loading ? <CircularProgress size={14} /> : label}
                    </Button>
                ))}
                <Button
                    size={buttonSize}
                    color="error"
                    variant="contained"
                    disabled={loading}
                    onClick={openDeleteDialog}
                >
                    {loading ? <CircularProgress size={14} sx={{ color: 'white' }} /> : 'Supprimer le commentaire'}
                </Button>
            </Stack>

            <Dialog
                open={Boolean(statusDialog)}
                onClose={() => !loading && closeStatusDialog()}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Confirmer le changement de statut</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                        Marquer ce signalement comme &quot;{findStatusLabel(statusDialog?.status)}&quot;.
                        Vous pouvez laisser une note interne (optionnel).
                    </DialogContentText>
                    <TextField
                        multiline
                        minRows={3}
                        label="Note interne"
                        value={statusNote}
                        onChange={(event) => setStatusNote(event.target.value)}
                        fullWidth
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeStatusDialog} disabled={loading}>
                        Annuler
                    </Button>
                    <Button onClick={submitStatusChange} disabled={loading} variant="contained">
                        {loading ? <CircularProgress size={18} sx={{ color: 'white' }} /> : 'Confirmer'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={deleteDialogOpen}
                onClose={() => !loading && closeDeleteDialog()}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Supprimer le commentaire signalé</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                        L&apos;action est définitive. Fournissez un contexte pour la modération.
                    </DialogContentText>
                    <TextField
                        multiline
                        minRows={3}
                        label="Motif de suppression"
                        value={deleteReason}
                        onChange={(event) => setDeleteReason(event.target.value)}
                        fullWidth
                        required
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeDeleteDialog} disabled={loading}>
                        Annuler
                    </Button>
                    <Button
                        onClick={submitDeleteAction}
                        disabled={loading || !deleteReason.trim()}
                        variant="contained"
                        color="error"
                    >
                        {loading ? <CircularProgress size={18} sx={{ color: 'white' }} /> : 'Supprimer'}
                    </Button>
                </DialogActions>
            </Dialog>

            {variant === 'show' && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    Les actions sont journalisées côté backend (statut, suppression, avertissements).
                </Typography>
            )}
        </>
    );
}
